import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { 
  Button, 
  Stack, 
  TextField, 
  Typography, 
  Paper, 
  Box,
  InputAdornment,
  Chip
} from '@mui/material';
import { 
  Navigation, 
  MyLocation, 
  Clear,
  East,
  North
} from '@mui/icons-material';
import { innerColor, themeColor, modernDesignSystem } from '../constants/themes';
import { toast } from 'react-toastify';

const CoordinateSearchUtil = () => {
  const { map } = useSelector((state) => state.globalMaps[0]);

  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  /*   if (latitude < 390000 || latitude > 610000) {
    setLatitude('');
  }
  if (longitude < 4000000 || longitude > 4200000) {
    setLongitude('');
  } */

  const setView = () => {
    // Koordinat doğrulaması
    const xCoord = parseFloat(latitude);  // X koordinatı (Sağa değer)
    const yCoord = parseFloat(longitude); // Y koordinatı (Yukarı değer)
    
    if (xCoord < 390000 || xCoord > 610000) {
      toast.error('Sağa değer (X) 390000-610000 arasında olmalıdır!', {
        position: 'top-center',
        theme: 'dark',
      });
      return;
    }
    
    if (yCoord < 4000000 || yCoord > 4200000) {
      toast.error('Yukarı değer (Y) 4000000-4200000 arasında olmalıdır!', {
        position: 'top-center',
        theme: 'dark',
      });
      return;
    }

    toast.success('Koordinata erişildi.', {
      position: 'top-center',
      theme: 'dark',
    });
    
    // UTM Zone 37 koordinatları: [X, Y] formatında
    map.getView().animate({
      center: [xCoord, yCoord], // Doğru sıralama: [X, Y]
      zoom: 15,
      duration: 2000,
    });
  };

  const clearCoordinates = () => {
    setLatitude('');
    setLongitude('');
  };
  return (
    <Box sx={{ padding: modernDesignSystem.spacing.xs }}>
      {/* Kompakt Başlık Bölümü */}
      <Paper
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${modernDesignSystem.colors.tertiary.surface} 0%, ${modernDesignSystem.colors.neutral.gray50} 100%)`,
          padding: modernDesignSystem.spacing.sm,
          borderRadius: modernDesignSystem.borders.radius.small,
          marginBottom: modernDesignSystem.spacing.sm,
          border: `1px solid ${modernDesignSystem.colors.tertiary.light}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: 0.5 }}>
          <Navigation sx={{ color: modernDesignSystem.colors.tertiary.main, fontSize: '1.1rem' }} />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: modernDesignSystem.colors.tertiary.dark,
              fontSize: modernDesignSystem.typography.fontSize.base,
            }}
          >
            Koordinat Arama
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: modernDesignSystem.colors.neutral.gray600,
            fontSize: modernDesignSystem.typography.fontSize.xs,
          }}
        >
          UTM Zone 37 koordinat sistemi ile arama
        </Typography>
      </Paper>

      <Stack spacing={modernDesignSystem.spacing.sm}>
        {/* Koordinat Sistemi Bilgi */}
        <Paper
          elevation={0}
          sx={{
            padding: modernDesignSystem.spacing.sm,
            backgroundColor: modernDesignSystem.colors.neutral.gray50,
            borderRadius: modernDesignSystem.borders.radius.small,
            border: `1px solid ${modernDesignSystem.colors.neutral.gray200}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
            <MyLocation sx={{ color: modernDesignSystem.colors.tertiary.main, fontSize: '1rem' }} />
            <Typography
              variant="caption"
              sx={{
                color: modernDesignSystem.colors.neutral.gray700,
                fontWeight: 600,
              }}
            >
              UTM Zone 37N Koordinat Sistemi
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="X: 390.000 - 610.000"
              size="small"
              sx={{
                backgroundColor: modernDesignSystem.colors.tertiary.surface,
                color: modernDesignSystem.colors.tertiary.dark,
                fontSize: '0.65rem',
                height: '22px',
              }}
            />
            <Chip
              label="Y: 4.000.000 - 4.200.000"
              size="small"
              sx={{
                backgroundColor: modernDesignSystem.colors.tertiary.surface,
                color: modernDesignSystem.colors.tertiary.dark,
                fontSize: '0.65rem',
                height: '22px',
              }}
            />
          </Box>
        </Paper>

        {/* X Koordinat (Sağa Değer) */}
        <TextField
          value={latitude}
          onChange={(e) => setLatitude(e.target.value)}
          type="number"
          label="Sağa Değer (X)"
          placeholder="390000 - 610000"
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            inputProps: { min: 390000, max: 610000 },
            startAdornment: (
              <InputAdornment position="start">
                <East sx={{ color: modernDesignSystem.colors.tertiary.main, fontSize: '1rem' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: modernDesignSystem.colors.neutral.white,
            '& .MuiOutlinedInput-root': {
              borderRadius: modernDesignSystem.borders.radius.small,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: modernDesignSystem.colors.tertiary.light,
              borderWidth: '2px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: modernDesignSystem.colors.tertiary.main,
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: modernDesignSystem.colors.tertiary.dark,
            }
          }}
        />

        {/* Y Koordinat (Yukarı Değer) */}
        <TextField
          value={longitude}
          onChange={(e) => setLongitude(e.target.value)}
          type="number"
          label="Yukarı Değer (Y)"
          placeholder="4000000 - 4200000"
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            inputProps: { min: 4000000, max: 4200000 },
            startAdornment: (
              <InputAdornment position="start">
                <North sx={{ color: modernDesignSystem.colors.tertiary.main, fontSize: '1rem' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: modernDesignSystem.colors.neutral.white,
            '& .MuiOutlinedInput-root': {
              borderRadius: modernDesignSystem.borders.radius.small,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: modernDesignSystem.colors.tertiary.light,
              borderWidth: '2px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: modernDesignSystem.colors.tertiary.main,
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: modernDesignSystem.colors.tertiary.dark,
            }
          }}
        />

        {/* Butonlar */}
        <Stack spacing={modernDesignSystem.spacing.xs}>
          <Button
            variant="contained"
            fullWidth
            disabled={!(!!latitude && !!longitude)}
            startIcon={<MyLocation />}
            size="small"
            onClick={setView}
            sx={{
              background: `linear-gradient(135deg, ${modernDesignSystem.colors.tertiary.main} 0%, ${modernDesignSystem.colors.tertiary.dark} 100%)`,
              color: modernDesignSystem.colors.neutral.white,
              borderRadius: modernDesignSystem.borders.radius.small,
              padding: `${modernDesignSystem.spacing.sm} ${modernDesignSystem.spacing.md}`,
              fontSize: modernDesignSystem.typography.fontSize.sm,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: modernDesignSystem.shadows.medium,
              
              '&:hover:not(:disabled)': {
                background: `linear-gradient(135deg, ${modernDesignSystem.colors.tertiary.dark} 0%, ${modernDesignSystem.colors.primary.dark} 100%)`,
                boxShadow: modernDesignSystem.shadows.elevated,
                transform: 'translateY(-1px)',
              },
              
              '&:disabled': {
                background: modernDesignSystem.colors.neutral.gray300,
                color: modernDesignSystem.colors.neutral.gray500,
                boxShadow: 'none',
              }
            }}
          >
            Koordinata Git
          </Button>

          {(latitude || longitude) && (
            <Button
              onClick={clearCoordinates}
              variant="outlined"
              fullWidth
              startIcon={<Clear />}
              size="small"
              sx={{
                borderColor: modernDesignSystem.colors.tertiary.main,
                color: modernDesignSystem.colors.tertiary.main,
                borderRadius: modernDesignSystem.borders.radius.small,
                fontSize: modernDesignSystem.typography.fontSize.xs,
                fontWeight: 500,
                textTransform: 'none',
                borderWidth: '2px',
                
                '&:hover': {
                  borderColor: modernDesignSystem.colors.tertiary.dark,
                  backgroundColor: modernDesignSystem.colors.tertiary.surface,
                  color: modernDesignSystem.colors.tertiary.dark,
                  borderWidth: '2px',
                }
              }}
            >
              Temizle
            </Button>
          )}
        </Stack>

        {/* Koordinat Önizleme */}
        {latitude && longitude && (
          <Paper
            elevation={0}
            sx={{
              padding: modernDesignSystem.spacing.sm,
              backgroundColor: modernDesignSystem.colors.tertiary.surface,
              borderRadius: modernDesignSystem.borders.radius.small,
              border: `1px solid ${modernDesignSystem.colors.tertiary.light}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: modernDesignSystem.colors.tertiary.dark,
                fontWeight: 600,
                display: 'block',
                marginBottom: 0.5,
              }}
            >
              Girilen Koordinat:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: modernDesignSystem.colors.neutral.gray700,
                fontSize: modernDesignSystem.typography.fontSize.xs,
                fontFamily: 'monospace',
              }}
            >
              X: {latitude} | Y: {longitude}
            </Typography>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default CoordinateSearchUtil;
